/**
 * In-memory state machine сессии редактирования Р7 (WUI-316).
 *
 * Хранит фазы save-цикла по `documentKey` / `documentSessionId`, таймером `awaiting_ws`
 * и обработкой WS callback DS. Promote на getFullData — по `dsDirty` в DS-снимке (WUI-344 §7).
 *
 * Не переживает F5 (R10): после перезагрузки формы — новая сессия, staging не восстанавливается.
 *
 * @see dev/WUI-316.md §4.1
 * @see dev/WUI-344.md
 */
import type { FileItem } from '@/common/types/fileItem'
import {
  isOfficeCallbackNoChanges,
  isOfficeCallbackSaveReady,
  isOfficeCallbackTerminalError,
  type OfficeCallbackStatusEvent,
} from '@/services/r7AttachmentEditor.service'
import { resolveAttachmentRowCodeVersion } from '@/common/utils/fileAttachmentsUniData'
import { resolveAttachmentDocumentSessionId } from '@/common/utils/dsAttachmentSnapshot'
import type { DsAttachmentPhase } from '@/common/utils/dsAttachmentPhases'
import { t } from '@/common/utils/translations'

export { resolveAttachmentDocumentSessionId }
import { logR7OfficeCallbackWsDebug } from '@/common/utils/attachmentDebugLog'

/** Фаза save-цикла одного открытия Р7 для строки вложения. */
export type R7AttachmentEditPhase =
  | 'idle'
  | 'editing'
  | 'awaiting_ws'
  | 'staged'
  | 'error'

/** Снимок in-memory сессии; ключ store — `documentKey` из prepare. */
export type R7AttachmentEditSession = {
  elementId: number
  code: number
  version: number
  documentKey: string
  documentSessionId: string
  fileName?: string
  /** Режим DocEditor при открытии диалога (WUI-316 / view-only). */
  editorMode: 'edit' | 'view'
  phase: R7AttachmentEditPhase
  wasDirty: boolean
  /**
   * DS-фаза ленты до open (restore при close без dirty).
   * Не путать с `phase` сессии (save-цикл).
   */
  preOpenDsPhase?: DsAttachmentPhase
  waitDeadlineMs?: number
  lastStorageUri?: string
  lastWsStatus?: number
  /** После discard / lost / terminal error — поздние WS игнорируются */
  abandoned?: boolean
  editorCloseHandled?: boolean
}

/** Параметры `registerEditingSession` после успешного `prepare`. */
export type RegisterEditingSessionParams = {
  elementId: number
  code: number
  version: number
  documentKey: string
  documentSessionId: string
  fileName?: string
  initialStorageUri?: string | null
  mode: 'edit' | 'view'
  /** Фаза бейджа до open — restore при clean close. */
  preOpenDsPhase?: DsAttachmentPhase
}

/** Результат `onWsCallback`: `ignored` — не обновлять store/dispatch UI. */
export type OnWsCallbackResult = {
  ignored: boolean
  session: R7AttachmentEditSession | null
}

const sessionsByDocumentKey = new Map<string, R7AttachmentEditSession>()
const sessionsByDocumentSessionId = new Map<string, R7AttachmentEditSession>()
const timeoutTimers = new Map<string, ReturnType<typeof setTimeout>>()
const timeoutHandlersByElementId = new Map<
  number,
  (session: R7AttachmentEditSession) => Promise<void>
>()

function defaultWaitMs(): number {
  const raw = import.meta.env.VITE_R7_CALLBACK_WAIT_MS
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 45_000
}

/** Таймаут ожидания WS 2/6 / document-sync-state (`VITE_R7_CALLBACK_WAIT_MS`, default 45s). */
export function resolveR7CallbackWaitMs(): number {
  return defaultWaitMs()
}

function clearAwaitingTimer(documentKey: string): void {
  const timer = timeoutTimers.get(documentKey)
  if (timer != null) {
    clearTimeout(timer)
    timeoutTimers.delete(documentKey)
  }
}

function scheduleAwaitingTimer(session: R7AttachmentEditSession): void {
  clearAwaitingTimer(session.documentKey)
  const waitMs = defaultWaitMs()
  session.waitDeadlineMs = Date.now() + waitMs
  const handler = timeoutHandlersByElementId.get(session.elementId)
  if (!handler) return

  timeoutTimers.set(
    session.documentKey,
    setTimeout(() => {
      void handler(session).catch((error) => {
        console.warn('[R7 attachment] awaiting_ws timeout handler failed', error)
      })
    }, waitMs),
  )
}

/** Регистрирует обработчик timeout `awaiting_ws` для FILEATTACHMENTS (`elementId`). */
export function registerR7AwaitingWsTimeoutHandler(
  elementId: number,
  handler: (session: R7AttachmentEditSession) => Promise<void>,
): void {
  timeoutHandlersByElementId.set(elementId, handler)
}

/** Снимает обработчик timeout для элемента. */
export function unregisterR7AwaitingWsTimeoutHandler(elementId: number): void {
  timeoutHandlersByElementId.delete(elementId)
}

/** Перезапускает таймер `VITE_R7_CALLBACK_WAIT_MS` в фазе `awaiting_ws` (диалог «Подождать»). */
export function resetAwaitingWsTimer(documentKey: string): void {
  const session = sessionsByDocumentKey.get(documentKey)
  if (!session || session.phase !== 'awaiting_ws') return
  scheduleAwaitingTimer(session)
}

function clearPriorSessionsForRow(
  elementId: number,
  code: number,
  version: number,
  exceptDocumentKey: string,
): void {
  for (const [documentKey, session] of [...sessionsByDocumentKey.entries()]) {
    if (documentKey === exceptDocumentKey) continue
    if (session.elementId === elementId && session.code === code && session.version === version) {
      clearSession(documentKey)
    }
  }
}

/**
 * Создаёт сессию после `prepare` (open/view Р7).
 * Заменяет предыдущие сессии той же строки (re-open после error).
 */
export function registerEditingSession(params: RegisterEditingSessionParams): R7AttachmentEditSession {
  clearPriorSessionsForRow(
    params.elementId,
    params.code,
    params.version,
    params.documentKey,
  )
  clearAwaitingTimer(params.documentKey)

  const session: R7AttachmentEditSession = {
    elementId: params.elementId,
    code: params.code,
    version: params.version,
    documentKey: params.documentKey,
    documentSessionId: params.documentSessionId,
    fileName: params.fileName,
    editorMode: params.mode,
    lastStorageUri: params.initialStorageUri ? String(params.initialStorageUri).trim() : undefined,
    // Open сам по себе не «editing» для бейджа; editing — только после dirty.
    phase: 'idle',
    wasDirty: false,
    preOpenDsPhase: params.preOpenDsPhase ?? 'idle',
    abandoned: false,
    editorCloseHandled: false,
  }

  sessionsByDocumentKey.set(params.documentKey, session)
  sessionsByDocumentSessionId.set(params.documentSessionId, session)
  return session
}

/** Сессия по стабильному `documentSessionId` (R9). */
export function getEditingSessionByDocumentSessionId(
  documentSessionId: string,
): R7AttachmentEditSession | undefined {
  const id = String(documentSessionId ?? '').trim()
  if (!id) return undefined
  return sessionsByDocumentSessionId.get(id)
}

/**
 * Ищет сессию для строки ленты только по `documentSessionId` (поля или DS-снимок).
 * Используется в `attachmentRpcPolicy` для блокировок open/preview/getFullData.
 */
export function findSessionForAttachmentItem(
  item: FileItem,
  elementId?: number | null,
): R7AttachmentEditSession | undefined {
  const documentSessionId = resolveAttachmentDocumentSessionId(item, elementId)
  if (!documentSessionId) return undefined
  return getEditingSessionByDocumentSessionId(documentSessionId)
}

/** Актуальный `documentKey` сессии по `documentSessionId` строки (после R5-миграции). */
export function syncEditingDocumentKeyForAttachmentItem(
  item: FileItem,
  elementId?: number | null,
): string | null {
  const session = findSessionForAttachmentItem(item, elementId)
  const key = session?.documentKey?.trim()
  return key || null
}

/** Сессия по ключу DS (`documentKey` из prepare). */
export function getEditingSession(documentKey: string): R7AttachmentEditSession | undefined {
  return sessionsByDocumentKey.get(documentKey)
}

/**
 * Сессия для `onEditorClosed`: по `documentKey` из prepare или fallback по строке вложения.
 * После WS R5 `documentKey` мигрирует — `r7ActiveDocumentKey` в UI может быть устаревшим.
 */
export function resolveEditingSessionForClose(
  documentKey: string | null | undefined,
  item?: FileItem | null,
  elementId?: number | null,
): R7AttachmentEditSession | undefined {
  const key = String(documentKey ?? '').trim()
  if (key) {
    const byKey = sessionsByDocumentKey.get(key)
    if (byKey) return byKey
  }
  if (item && elementId != null && Number(elementId) > 0) {
    return findSessionForAttachmentItem(item, elementId)
  }
  return undefined
}

/** DocEditor `onDocumentStateChange(true)` — были правки; из `staged` возвращает в `editing`. */
export function setSessionWasDirty(documentKey: string): void {
  const session = sessionsByDocumentKey.get(documentKey)
  if (!session || session.phase === 'error' || session.abandoned) return
  session.wasDirty = true
  if (session.phase === 'staged') {
    session.phase = 'editing'
  }
}

/**
 * Destroy DocEditor after successful forcesave: keep `staged` for promote, no `awaiting_ws`.
 */
export function markEditorClosedKeepStaged(documentKey: string): void {
  const key = String(documentKey ?? '').trim()
  if (!key) return
  const session = sessionsByDocumentKey.get(key)
  if (!session || session.abandoned || session.phase === 'error') return
  session.editorCloseHandled = true
  session.wasDirty = false
  if (session.phase !== 'staged') {
    session.phase = 'staged'
  }
  clearAwaitingTimer(key)
}

/**
 * Закрытие DocEditor (dialog / preview).
 * Edit + `wasDirty` → `awaiting_ws` + таймер; view или без правок → session `idle`
 * (DS-фазу restore делает `finalizeR7AttachmentEditorClose`).
 */
export function onEditorClosed(documentKey: string): R7AttachmentEditSession | null {
  const session = sessionsByDocumentKey.get(documentKey)
  if (!session) return null
  if (session.editorCloseHandled) return session

  session.editorCloseHandled = true

  if (session.editorMode === 'view' || !session.wasDirty) {
    session.phase = 'idle'
    clearAwaitingTimer(documentKey)
    return session
  }

  session.phase = 'awaiting_ws'
  scheduleAwaitingTimer(session)
  return session
}

/** Точное совпадение `elementId`, `code`, `version`. */
export function findSessionForAttachmentRow(
  elementId: number,
  code: number,
  version: number,
): R7AttachmentEditSession | undefined {
  for (const session of sessionsByDocumentKey.values()) {
    if (session.elementId === elementId && session.code === code && session.version === version) {
      return session
    }
  }
  return undefined
}

/** Блок строки для getFullData: `awaiting_ws` или `error`. */
export function isRowBlockedByR7Session(
  elementId: number,
  code: number,
  version: number,
): boolean {
  const session = findSessionForAttachmentRow(elementId, code, version)
  if (!session) return false
  return session.phase === 'awaiting_ws' || session.phase === 'error'
}

/** Текст ошибки для toast при блокировке getFullData по строке. */
export function getR7EditBlockReasonForRow(
  elementId: number,
  code: number,
  version: number,
): string | null {
  const session = findSessionForAttachmentRow(elementId, code, version)
  if (!session) return null
  if (session.phase === 'awaiting_ws') {
    return t('attachments.r7WaitFormSave')
  }
  if (session.phase === 'error') {
    return t('attachments.r7EditSaveFailed')
  }
  return null
}

/** `true`, если поздние WS должны игнорироваться (`abandoned` / `error`). */
export function wasWsIgnoredForSession(documentKey: string | null): boolean {
  if (!documentKey) return false
  const session = sessionsByDocumentKey.get(documentKey)
  if (!session) return false
  return session.abandoned === true || session.phase === 'error'
}

/**
 * Обработка WS `OfficeCallbackHandler.statusChanged` (из `officeEventRouter` или fallback).
 * Переводы phase, mark/clear promote candidate; guard A1 (WS 2/6 без dirty).
 */
function migrateSessionDocumentKey(session: R7AttachmentEditSession, newDocumentKey: string): void {
  const nextKey = String(newDocumentKey ?? '').trim()
  if (!nextKey || session.documentKey === nextKey) return
  const prevKey = session.documentKey
  const timer = timeoutTimers.get(prevKey)
  if (timer != null) {
    timeoutTimers.delete(prevKey)
    timeoutTimers.set(nextKey, timer)
  }
  sessionsByDocumentKey.delete(prevKey)
  session.documentKey = nextKey
  sessionsByDocumentKey.set(nextKey, session)
}

export function onWsCallback(event: OfficeCallbackStatusEvent): OnWsCallbackResult {
  const newDocumentKey = event.documentKey?.trim() || null
  const documentSessionId = event.documentSessionId.trim()
  let session: R7AttachmentEditSession | null =
    sessionsByDocumentSessionId.get(documentSessionId) ?? null

  if (!session && newDocumentKey) {
    session = sessionsByDocumentKey.get(newDocumentKey) ?? null
  }

  if (session && newDocumentKey && session.documentKey !== newDocumentKey) {
    migrateSessionDocumentKey(session, newDocumentKey)
  }

  if (!session) {
    return { ignored: false, session: null }
  }

  if (session.abandoned || session.phase === 'error') {
    logR7OfficeCallbackWsDebug('ws-drop', {
      documentKey: session.documentKey,
      syncState: event.syncState,
      dsStatus: event.dsStatus,
      phase: session.phase,
    })
    return { ignored: true, session }
  }

  if (event.storageUri) {
    session.lastStorageUri = event.storageUri
  }
  if (event.dsStatus != null) {
    session.lastWsStatus = event.dsStatus
  }

  if (isOfficeCallbackTerminalError(event)) {
    session.phase = 'error'
    session.abandoned = true
    clearAwaitingTimer(session.documentKey)
    return { ignored: false, session }
  }

  if (event.syncState === 'syncing' || event.syncState === 'stale') {
    return { ignored: false, session }
  }

  if (isOfficeCallbackNoChanges(event)) {
    session.phase = 'idle'
    clearAwaitingTimer(session.documentKey)
    return { ignored: false, session }
  }

  if (isOfficeCallbackSaveReady(event)) {
    const canPromoteBySaveCallback =
      session.editorMode === 'edit'
      && (session.wasDirty || session.phase === 'awaiting_ws' || session.phase === 'staged')
    if (!canPromoteBySaveCallback) {
      logR7OfficeCallbackWsDebug('ws-drop', {
        reason: 'skip-promote-save-callback-no-dirty',
        documentKey: session.documentKey,
        elementId: session.elementId,
        code: session.code,
        version: session.version,
        syncState: event.syncState,
        dsStatus: event.dsStatus,
        sessionPhase: session.phase,
        wasDirty: session.wasDirty,
      })
      return { ignored: false, session }
    }
    session.phase = 'staged'
    session.wasDirty = false
    clearAwaitingTimer(session.documentKey)
  }

  return { ignored: false, session }
}

/** Удаляет сессию и таймер (после успешного promote на getFullData). */
export function clearSession(documentKey: string): void {
  clearAwaitingTimer(documentKey)
  const session = sessionsByDocumentKey.get(documentKey)
  if (session?.documentSessionId) {
    sessionsByDocumentSessionId.delete(session.documentSessionId)
  }
  sessionsByDocumentKey.delete(documentKey)
}

/** Unmount FILEATTACHMENTS — сброс edit-сессий элемента (диалог/форма закрыты). */
export function clearSessionsForElement(elementId: number): void {
  const eid = Number(elementId)
  if (!Number.isFinite(eid) || eid <= 0) return
  for (const [documentKey, session] of [...sessionsByDocumentKey.entries()]) {
    if (session.elementId === eid) {
      clearSession(documentKey)
    }
  }
  unregisterR7AwaitingWsTimeoutHandler(eid)
}

/** `clearSession` по `elementId+code+version`. */
export function clearSessionForAttachmentRow(
  elementId: number,
  code: number,
  version: number,
): void {
  const session = findSessionForAttachmentRow(elementId, code, version)
  if (session) clearSession(session.documentKey)
}

/**
 * Reconcile после `POST document-sync-state` → `syncState: ready` (timeout без WS).
 * Эквивалент успешного WS `2`/`6`: `staged` (promote — по `dsDirty` в DS-снимке).
 */
export function reconcileSessionStaged(
  documentKey: string,
  storageUri: string | null | undefined,
  dsStatus?: number,
): void {
  const session = sessionsByDocumentKey.get(documentKey)
  if (!session || session.abandoned) return

  session.phase = 'staged'
  session.wasDirty = false
  if (storageUri) session.lastStorageUri = storageUri
  if (dsStatus) session.lastWsStatus = dsStatus
  clearAwaitingTimer(documentKey)
}

/** Terminal error: discard, sync-state error / ds 3|7, сбой sync-state API. */
export function markSessionErrorAbandoned(documentKey: string): void {
  const session = sessionsByDocumentKey.get(documentKey)
  if (!session) return
  session.phase = 'error'
  session.abandoned = true
  clearAwaitingTimer(documentKey)
}

/** @internal */
export function __clearR7AttachmentEditSessionsForTests(): void {
  for (const key of sessionsByDocumentKey.keys()) {
    clearAwaitingTimer(key)
  }
  sessionsByDocumentKey.clear()
  sessionsByDocumentSessionId.clear()
  timeoutHandlersByElementId.clear()
}
